import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, MapPin, LayoutGrid, Sun, Trees,
  Sparkles, Leaf, Briefcase, GraduationCap, Award, BadgeCheck, Wallet,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, WA_MESSAGE } from "../data/site";

const EVALUATION_FACTORS = [
  { icon: MapPin, title: "Location & Connectivity", desc: "Distance to metro, airport, main roads and your daily destinations." },
  { icon: LayoutGrid, title: "Apartment Planning", desc: "Efficient layouts that minimise wasted circulation space." },
  { icon: Sun, title: "Natural Light & Ventilation", desc: "Open views and cross ventilation keep homes bright and airy." },
  { icon: Trees, title: "Open & Green Spaces", desc: "Parks, gardens and tree cover inside and around the project." },
  { icon: Sparkles, title: "Amenities", desc: "Facilities your family will genuinely use every week." },
  { icon: Leaf, title: "Sustainability", desc: "Green-building certification, water and energy efficiency." },
  { icon: Briefcase, title: "Employment Hubs", desc: "Proximity to IT and business districts shortens commutes." },
  { icon: GraduationCap, title: "Schools & Healthcare", desc: "Reputed schools and hospitals within easy reach." },
  { icon: Award, title: "Developer Track Record", desc: "Delivery history, scale and resident satisfaction." },
  { icon: BadgeCheck, title: "RERA Verification", desc: "Always verify the project and agent registration on the WBRERA website." },
  { icon: Wallet, title: "Budget & Configuration", desc: "Match the configuration to your family's real needs and budget." },
];

const WHY_21_ACRES = [
  "Located at Hatishala, adjacent to Newtown Action Area III",
  "2 & 3 BHK residences across five towers",
  "90% homes with 3-side open views",
  "Double-height sky terraces",
  "Vastu-compliant layouts",
  "Planned around natural light and cross ventilation",
  "Efficient floor plans with reduced circulation space",
  "IGBC Precertified Platinum Rated Green Building",
  "3-acre central park with gardens, courts and trails",
  "Shuttle service to the nearest metro planned",
];

const GUIDE_FAQS = [
  {
    q: "Which is the best property in New Town Kolkata?",
    a: "There is no single best property for every buyer — the right choice depends on your budget, preferred configuration, location priorities, amenities and individual requirements. That said, buyers exploring the Action Area III vicinity may find Vinayak 21 Acres worth considering for its verified features: 90% homes with 3-side open views, double-height sky terraces, an IGBC Precertified Platinum green rating and a 3-acre central park.",
  },
  {
    q: "What should I check before buying property in New Town?",
    a: "Evaluate location and connectivity, apartment planning, natural light and ventilation, open and green spaces, amenities, sustainability features, distance to employment hubs, nearby schools and healthcare, the developer's track record, and always verify the project's RERA registration before booking.",
  },
  {
    q: "Are 2 BHK and 3 BHK homes available near Action Area III?",
    a: "Yes. Vinayak 21 Acres at Hatishala, adjacent to Newtown Action Area III, offers 2 BHK 2T residences and 3 BHK homes in Smart, Comfort and Luxury configurations across five towers.",
  },
  {
    q: "What should I look for in a gated residential project?",
    a: "Look for security features such as CCTV in common areas and video door phones, genuine open and green space, efficient floor plans, sustainability measures like rainwater harvesting and solar common lighting, high-speed elevators, and clear RERA registration for both the project and the agent you deal with.",
  },
  {
    q: "Where is Vinayak 21 Acres located?",
    a: "Vinayak 21 Acres is located at Hatishala, adjacent to Newtown Action Area III, Kolkata — close to the IT hub, schools, hospitals and daily conveniences of New Town.",
  },
  {
    q: "What configurations are available at Vinayak 21 Acres?",
    a: "2 BHK 2T homes of approximately 595–597 sq ft carpet area, and 3 BHK homes in Smart (approx. 698–703 sq ft carpet), Comfort (approx. 736–783 sq ft) and Luxury (approx. 823–943 sq ft) families. Exact figures vary by tower, type and floor.",
  },
  {
    q: "How can I check current prices and availability?",
    a: "Prices and availability are subject to change. Contact Forest Hill Realtors through the enquiry buttons on this page, WhatsApp or a call for the latest developer-provided pricing and available inventory.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book a Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot for you.",
  },
];

export default function Guide() {
  const [modal, setModal] = useState({ open: false, source: "Guide Page Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Best Property in New Town Kolkata | Residential Property Guide";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Looking for the best property in New Town Kolkata? Explore what homebuyers should consider and discover Vinayak 21 Acres near Newtown Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="guide-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="guide-overline">Buyer's Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="guide-h1"
            >
              Best Property in <span className="text-[#84CC16]">New Town Kolkata</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              Searching for the best residential property in New Town Kolkata? There is no single right answer for
              every buyer — but there is a right way to choose. This guide walks you through what genuinely matters
              when comparing residential property in New Town Kolkata, and introduces one option near Action Area III
              that many buyers shortlist: Vinayak 21 Acres.
            </p>
          </div>
        </section>

        <section data-testid="guide-howto-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="The Framework" title="How to Choose the Best Property in New Town Kolkata" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                New Town has grown into one of Kolkata's most planned residential zones, and New Town Kolkata property
                choices range widely. Before comparing projects, score each one against these eleven factors — the
                same checklist our advisors use.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {EVALUATION_FACTORS.map((f, i) => (
                <Reveal key={f.title} delay={0.04 * i}>
                  <div data-testid={`guide-factor-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <f.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{f.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="guide-location-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="Connectivity" title="Location & Connectivity" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  For most buyers, the best property in Newtown starts with the daily commute. The Action Area III
                  corridor places residents minutes from major IT workplaces, with the nearest metro and the airport
                  within practical driving distance. Check indicative travel times rather than straight-line distance,
                  and confirm planned infrastructure like shuttle services.
                </p>
                <p>
                  Equally important are weekly errands — markets, malls, schools and hospitals. A location that keeps
                  these within 5–20 minutes dramatically improves everyday life.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-planning-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Inside the Home" title="Apartment Planning & Livability" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Two homes with the same SBUA can live very differently. Efficient floor plans reduce wasted passage
                  space, so more of what you pay for becomes usable room. Look for open-sided homes — windows on
                  multiple sides bring in daylight and enable cross ventilation, lowering dependence on artificial
                  cooling.
                </p>
                <p>
                  Many buyers also value Vastu-compliant orientations and outdoor extensions of the home, such as
                  balconies or terrace decks, that make compact plans feel larger.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-green-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="Outdoors" title="Green Spaces & Amenities" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Green cover inside a project is no longer a luxury — it shapes air quality, children's play and
                  weekend routines. Large central parks, theme gardens, walking and cycling trails, and sports courts
                  signal a community designed for daily outdoor life, not just a brochure render.
                </p>
                <p>
                  When comparing amenities, ask which ones your family will actually use weekly — a play area, jogging
                  track or pet park often matters more than a long list of rarely used facilities.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-sustainability-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="Future-Proofing" title="Sustainability" dark />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Green-certified buildings cost less to run and age better. Indicators worth checking: IGBC or
                  similar precertification, water-efficient fixtures, rainwater harvesting, solar-powered common
                  lighting, waste management, EV-charging provision and low-VOC paints.
                </p>
                <p>
                  These features reduce monthly maintenance pressure and are increasingly valued by future buyers and
                  tenants alike.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-why-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="06" eyebrow="One Option to Shortlist" title="Why Consider Vinayak 21 Acres?" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                If your search for the best property in New Town prioritises the Action Area III vicinity, Vinayak 21
                Acres is one relevant residential option to evaluate. Based on the project's published information, it
                offers:
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-3.5 mt-8 max-w-4xl">
              {WHY_21_ACRES.map((f, i) => (
                <Reveal key={f} delay={0.04 * i}>
                  <div data-testid={`guide-why-item-${i}`} className="flex items-start gap-3 bg-white border border-[#DCECC0] rounded-xl px-5 py-4">
                    <span className="text-[#84CC16] mt-0.5">✦</span>
                    <p className="text-sm text-[#365314] font-sub font-medium leading-relaxed">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <p className="mt-8 text-[#65A30D] text-sm leading-relaxed max-w-3xl">
                Whether it is the right fit depends on your budget, configuration and priorities — we recommend
                comparing it against your shortlist using the framework above.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-options-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="Configurations" title="2 BHK & 3 BHK Options" />
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <Reveal>
                <div className="bg-white border border-[#DCECC0] rounded-2xl p-8 h-full" data-testid="guide-2bhk-card">
                  <h3 className="font-serif-display text-2xl text-[#365314]">2 BHK 2T</h3>
                  <p className="text-sm text-[#4D7C0F] mt-3 leading-relaxed">
                    Approx. 595–597 sq ft carpet area with about 60 sq ft balcony, 728 sq ft BUA and 971 sq ft SBUA —
                    available as Type B and Type G across Towers 1A, 1B, 2A and 2B.
                  </p>
                  <button data-testid="guide-check-2bhk-btn" onClick={() => openModal("Availability Enquiry: 2 BHK", "2 BHK")}
                    className="mt-6 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="bg-white border border-[#DCECC0] rounded-2xl p-8 h-full" data-testid="guide-3bhk-card">
                  <h3 className="font-serif-display text-2xl text-[#365314]">3 BHK — Smart, Comfort & Luxury</h3>
                  <p className="text-sm text-[#4D7C0F] mt-3 leading-relaxed">
                    From approx. 698 sq ft carpet (Smart) to about 943 sq ft carpet (Luxury) — 2-toilet and 3-toilet
                    layouts across all five towers, with balcony, BUA and SBUA varying by odd/even sky-terrace floors.
                  </p>
                  <button data-testid="guide-check-3bhk-btn" onClick={() => openModal("Availability Enquiry: 3 BHK", "3 BHK")}
                    className="mt-6 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="guide-floorplan-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                </Link>
                <Link to="/" data-testid="guide-home-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Explore Vinayak 21 Acres <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-price-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="08" eyebrow="Budget" title="Price & Floor Plans" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8 shadow-[0_24px_48px_-24px_rgba(77,124,15,0.18)]">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                  Pricing at Vinayak 21 Acres varies by configuration, tower, floor, unit selection, applicable
                  charges and current availability. Prices and availability are subject to change — contact Forest
                  Hill Realtors for the latest developer-provided pricing and available inventory.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="guide-get-price-btn" onClick={() => openModal("Get Latest Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest Price
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="guide-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-visit-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">09 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  The most reliable way to judge any property is to walk through it. See the light, the ventilation
                  and the 3-acre central park yourself before you decide.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="guide-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="guide-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="guide-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="10" eyebrow="FAQs" title="Buyer Questions, Answered" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="guide-faq-accordion">
                {GUIDE_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`guide-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`guide-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="guide-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
